import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Container, useTheme } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import {
  selectForceFloatingTicketShowValidationError,
  selectForceShowValidationError,
} from 'store/fieldValidationsSlice';
import { QUERIES_KEYS } from 'constant';
import { useApplicationData } from 'remote-state/applicationHooks';
import { useSR } from 'remote-state/ticketServiceHooks';
import { SESSION_STORAGE } from 'features/queue/constants';
import useHiddenSelectOption from 'common/utils/hooks/useHiddenSelectOption';
import { castCategoryDefaultToNumber } from 'features/templateBuilder/utils/utils';
import { getIsExistingSr } from 'common/utils/srUtils';
import { useGetSuggestedCategories } from 'features/queue/grid/hooks/useGetSuggestedCategories';
import { useSuggestedCategoryDropdownControl } from 'features/suggestedCategory/suggestedCategoryHooks';
import { VirtualizedScrollContext } from 'common/components/dropdownSelect/VirtualizedScroll/VirtualizedScrollContext';
import { DisplayPage } from 'common/utils/displayPage';
import { actionTypes, useCategory } from 'store/CategoryContext';
import { getCustomHeaderStyles, StyledCategories } from './style';
import { CategorySelect } from './categorySelect';
import {
  CATEGORIES,
  DELETED_CATEGORY_ID,
  FIRST_LEVEL_CATEGORY_FIELD_ID,
  SECOND_LEVEL_CATEGORY_FIELD_ID,
  THIRD_LEVEL_CATEGORY_FIELD_ID,
  TO_BE_DETERMINED,
} from './constants';
import useTexts from './useTexts';
import { useOpenListIfReady } from './useOpenListIfReady';
import { HeaderItemLabel, LabelWrap } from '../style';
import {
  getCategoryDrivenTemplate,
  getCategoryDrivenTemplateWithoutThirdLevel,
} from '../../../services/templateService';
import { SR_TYPE_CODE_MAP } from '../../TicketPanel/constants';
import { useCategoryDrivenEnabled } from '../../../remote-state/accountSettingsServiceHooks';
import { selectIsLoading } from '../../../store/categorySlice';
import { getCategoryFieldsMap } from './utils';
import { useModifiedCategoriesAttributes } from '../../../remote-state/category/useModifiedCategoriesAttributes';

function Categories(props) {
  const {
    disabled,
    onDisabledClick,
    dataset,
    isTemplatePage,
    headerFields,
    srType,
    handleDirty,
    onFocus,
    handleSaveValue,
    required,
    updateFieldValidation,
    className = '',
    categoriesValues,
    forceSrId = null,
    onTemplateChanged,
    isNewSR = false,
  } = props;
  const {
    sr: { data: sr },
  } = useSR(forceSrId);
  const { state: categoryData, dispatch: categoryDispatch } = useCategory();

  const selectedCategories = useMemo(() => {
    const { primaryCategory, secondaryCategory, thirdLevelCategory } = categoryData;
    return { primaryCategory, secondaryCategory, thirdLevelCategory };
  }, [categoryData]);

  const isExistingSR = getIsExistingSr(sr?.id);
  const [isPrimaryListOpen, setIsPrimaryListOpen] = useState(false);
  const [isSecondaryListOpen, setIsSecondaryListOpen] = useState(false);
  const [isThirdListOpen, setIsThirdListOpen] = useState(false);
  const [isSecondaryListReady, setIsSecondaryListReady] = useState(false);
  const [isThirdListReady, setIsThirdListReady] = useState(false);
  const [clearedObj, setClearedObj] = useState({});
  const isLoadingDictionary = useSelector(selectIsLoading());
  const isLoading = useMemo(
    () =>
      isLoadingDictionary ||
      (!selectedCategories?.primaryCategory && sr?.category?.primaryCategory && isExistingSR && !clearedObj?.primary),
    [isLoadingDictionary, selectedCategories?.primaryCategory, isExistingSR, sr?.category, clearedObj],
  );
  const { requestOpen: requestOpenSecondaryList } = useOpenListIfReady({
    isListReady: isSecondaryListReady,
    isListOpen: isSecondaryListOpen,
    openList: () => setIsSecondaryListOpen(true),
  });
  const thirdLevelFocusWrapper = useCallback(() => {
    setIsThirdListOpen(true);
    onFocus?.(THIRD_LEVEL_CATEGORY_FIELD_ID);
  }, [onFocus]);
  const { requestOpen: requestOpenThirdLevelList } = useOpenListIfReady({
    isListReady: isThirdListReady,
    isListOpen: isThirdListOpen,
    openList: () => setTimeout(() => thirdLevelFocusWrapper(), 100),
  });

  const [showExitPrompt, setShowExitPrompt] = useState(false);
  const [shouldResetSelectedCategories, setShouldResetSelectedCategories] = useState(true);
  const [additionalBECategories, setAdditionalBECategories] = useState({ primary: [], secondary: [], third: [] });
  const [isDirty, setIsDirty] = useState(false);
  const [isInitializing, setIsInitializing] = useState(true);
  const [hasLoadedInitialCategories, setHasLoadedInitialCategories] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const { data: isTripleCategory } = useApplicationData(QUERIES_KEYS.IS_EXTERNAL_MODE);
  const {
    categoryText,
    subCategoryText,
    thirdLevelCategoryText,
    selectCategoryText,
    selectSubCategoryText,
    select3rdLevelCategoryText,
    categoriesText,
    secondCategoryClearDisabledTooltipText,
    thirdCategoryClearDisabledTooltipText,
  } = useTexts();
  const forceShowValidation = useSelector(
    isExistingSR ? selectForceShowValidationError : selectForceFloatingTicketShowValidationError,
  );
  const [
    primaryCategories,
    secondaryCategories,
    thirdLevelCategories,
    filteredPrimaryCategories,
    filteredSecondaryCategories,
    filteredThirdLevelCategories,
  ] = useModifiedCategoriesAttributes(
    'all',
    selectedCategories.primaryCategory,
    selectedCategories.secondaryCategory,
    srType,
  );
  const secondaryCategoriesMemo = useMemo(() => secondaryCategories, [secondaryCategories]);
  const thirdLevelCategoriesMemo = useMemo(() => thirdLevelCategories, [thirdLevelCategories]);
  const categoryFieldsMap = getCategoryFieldsMap(headerFields);
  const isCategoryDrivenEnabled = useCategoryDrivenEnabled()?.data?.data;
  const firstLevelCategoryDefault = castCategoryDefaultToNumber(
    categoryFieldsMap[CATEGORIES.FIRST_LEVEL_CATEGORY_FIELD_KEY]?.defaultValue,
  );
  const secondLevelCategoryDefault = castCategoryDefaultToNumber(
    categoryFieldsMap[CATEGORIES.SECOND_LEVEL_CATEGORY_FIELD_KEY]?.defaultValue,
  );
  const thirdLevelCategoryDefault = castCategoryDefaultToNumber(
    categoryFieldsMap[CATEGORIES.THIRD_LEVEL_CATEGORY_FIELD_KEY]?.defaultValue,
  );

  const selectedCategoriesDispatchTimeout = useRef();

  useEffect(() => {
    if (hasLoadedInitialCategories) return;

    selectedCategoriesDispatchTimeout.current = setTimeout(() => {
      categoryDispatch({
        type: 'SET_ALL',
        payload: {
          primaryCategory: categoriesValues?.primaryCategory,
          secondaryCategory: categoriesValues?.secondaryCategory,
          thirdLevelCategory: categoriesValues?.thirdLevelCategory,
        },
      });
      setHasLoadedInitialCategories(true);
      setIsInitializing(false);
    }, 500);
  }, [
    categoriesValues?.primaryCategory,
    categoriesValues?.secondaryCategory,
    categoriesValues?.thirdLevelCategory,
    categoryDispatch,
    hasLoadedInitialCategories,
  ]);

  useEffect(() => () => clearTimeout(selectedCategoriesDispatchTimeout.current), []);

  useEffect(() => {
    const safetyTimeout = setTimeout(() => {
      setIsInitializing(false);
    }, 1000);
    return () => clearTimeout(safetyTimeout);
  }, []);

  const theme = useTheme();
  const dispatch = useDispatch();

  const getCurrentValue = useCallback(
    (id, name, list) => {
      const currentCategory = list?.find((item) => String(item.id) === String(id));
      if (!currentCategory && name === TO_BE_DETERMINED) {
        return undefined;
      }

      if (!!id && name != null && !currentCategory && !isDirty) {
        return { id, name };
      }
      return currentCategory;
    },
    [isDirty],
  );

  const checkAndUpdateValidationStatus = useCallback(
    (primaryCategory, secondaryCategory, thirdLevelCategory) => {
      if (!updateFieldValidation || isTemplatePage) return;
      let isValid = true;
      const categoriesValidationObj = {
        primaryCategory: true,
        secondaryCategory: true,
        thirdLevelCategory: true,
      };
      if (!required) {
        // categories are not required
      } else if (
        primaryCategory === -1 ||
        primaryCategory === undefined ||
        primaryCategory === null ||
        // eslint-disable-next-line no-restricted-globals
        isNaN(primaryCategory)
      ) {
        categoriesValidationObj.primaryCategory = false;
        categoriesValidationObj.secondaryCategory = false;
        categoriesValidationObj.thirdLevelCategory = false;
      } else if (
        secondaryCategory === -1 ||
        secondaryCategory === undefined ||
        secondaryCategory === null ||
        // eslint-disable-next-line no-restricted-globals
        isNaN(secondaryCategory)
      ) {
        categoriesValidationObj.primaryCategory = true;
        categoriesValidationObj.secondaryCategory = false;
        categoriesValidationObj.thirdLevelCategory = false;
      } else if (
        isTripleCategory &&
        (thirdLevelCategory === -1 ||
          thirdLevelCategory === undefined ||
          thirdLevelCategory === null ||
          // eslint-disable-next-line no-restricted-globals
          isNaN(thirdLevelCategory))
      ) {
        categoriesValidationObj.primaryCategory = true;
        categoriesValidationObj.secondaryCategory = true;
        categoriesValidationObj.thirdLevelCategory = false;
      }

      updateFieldValidation('primaryCategory', required, categoriesValidationObj.primaryCategory);
      updateFieldValidation('secondaryCategory', required, categoriesValidationObj.secondaryCategory);
      updateFieldValidation(
        'thirdLevelCategory',
        required,
        !isTripleCategory || categoriesValidationObj.thirdLevelCategory,
      );

      const categoriesHaveValues = isTripleCategory
        ? categoriesValidationObj.primaryCategory &&
          categoriesValidationObj.secondaryCategory &&
          categoriesValidationObj.thirdLevelCategory
        : categoriesValidationObj.primaryCategory && categoriesValidationObj.secondaryCategory;

      if (required && !categoriesHaveValues) {
        isValid = false;
      }
      return isValid;
    },
    [required, isTripleCategory, updateFieldValidation, isTemplatePage],
  );

  const filterExistingCategoriesFromNewList = (newCategories, existingCategories) =>
    newCategories?.filter((v) => !existingCategories.some((pc) => pc.id === v.id)) || [];

  const handleSaveCategories = useCallback(
    async (updatedCategories) => {
      const categoriesForValidation = Object.values(updatedCategories);
      const isValid = checkAndUpdateValidationStatus(...categoriesForValidation);
      const updatedCategoriesValue = !isExistingSR
        ? updatedCategories
        : { category: JSON.stringify(updatedCategories) };

      handleSaveValue(updatedCategoriesValue);
      if (isValid || isTemplatePage) {
        setShowExitPrompt(false);
      } else {
        setShowExitPrompt(true);
      }
    },
    [checkAndUpdateValidationStatus, isExistingSR, isTemplatePage, handleSaveValue],
  );

  const handleChangePrimaryCategory = useCallback(
    (id, searchedCategoriesList) => {
      setClearedObj({});
      if (id === '-1') id = -1;
      const additionalPrimary = filterExistingCategoriesFromNewList(searchedCategoriesList, primaryCategories);
      const { secondaryCategory, thirdLevelCategory } = selectedCategories;
      const categoriesForValidation = [id, secondaryCategory, thirdLevelCategory];
      setAdditionalBECategories((state) => ({ ...state, primary: additionalPrimary }));
      setShouldResetSelectedCategories(false);
      checkAndUpdateValidationStatus(...categoriesForValidation);
      categoryDispatch({ type: actionTypes.SET_SELECTED_PRIMARY_CATEGORY, payload: id });
      categoryDispatch({ type: actionTypes.SET_SELECTED_SECONDARY_CATEGORY, payload: undefined });
      categoryDispatch({ type: actionTypes.SET_SELECTED_THIRD_LEVEL_CATEGORY, payload: undefined });
      setIsSecondaryListOpen(false);
      setIsThirdListOpen(false);
      setIsSecondaryListReady(false);
      setIsThirdListReady(false);
      setTimeout(() => {
        setIsSecondaryListReady(true);
        requestOpenSecondaryList();
      }, 100);
      if (isTemplatePage) {
        handleSaveCategories({ ...selectedCategories, primaryCategory: id });
      }
      setShowExitPrompt(true);

      if (handleDirty) {
        handleDirty({ primaryCategory: id });
      }
    },
    [
      handleDirty,
      primaryCategories,
      selectedCategories,
      checkAndUpdateValidationStatus,
      requestOpenSecondaryList,
      handleSaveCategories,
      isTemplatePage,
      categoryDispatch,
    ],
  );

  const handleChangeThirdLevelCategory = useCallback(
    async (id, searchedCategoriesList, forceDirty) => {
      if (id === '-1') id = -1;
      setClearedObj((prev) => ({ ...prev, third: false }));
      const additionalThird = filterExistingCategoriesFromNewList(searchedCategoriesList, thirdLevelCategories);
      const { primaryCategory, secondaryCategory } = selectedCategories;
      const categoriesForValidation = [primaryCategory, secondaryCategory, id];
      setAdditionalBECategories((state) => ({ ...state, third: additionalThird }));
      setTimeout(() => setIsThirdListOpen(false), 250);
      setIsThirdListReady(false);
      setShouldResetSelectedCategories(false);
      checkAndUpdateValidationStatus(...categoriesForValidation);
      categoryDispatch({ type: actionTypes.SET_SELECTED_THIRD_LEVEL_CATEGORY, payload: id });
      if (isDirty || forceDirty) {
        if (isCategoryDrivenEnabled) {
          const data = await getCategoryDrivenTemplate(
            selectedCategories.primaryCategory,
            selectedCategories.secondaryCategory,
            id,
            SR_TYPE_CODE_MAP[srType],
          );
          onTemplateChanged?.({ template: data, isCategoryDrivenChangedTemplate: true });
        }
        handleSaveCategories({ ...selectedCategories, thirdLevelCategory: id });
        sessionStorage.setItem(SESSION_STORAGE.categoryIsSelected, true);
      }
      if (handleDirty) {
        handleDirty({ thirdLevelCategory: id });
      }
    },
    [
      thirdLevelCategories,
      isDirty,
      selectedCategories,
      checkAndUpdateValidationStatus,
      handleSaveCategories,
      categoryDispatch,
      srType,
      onTemplateChanged,
      isCategoryDrivenEnabled,
      handleDirty,
    ],
  );

  const handleChangeSecondaryCategory = useCallback(
    async (id, searchedCategoriesList, forceDirty, isAutoComplete = false) => {
      if (id === '-1') id = -1;
      if (id === -1) {
        setClearedObj((prev) => ({ ...prev, secondary: false, third: false }));
      }
      const additionalSecondary = filterExistingCategoriesFromNewList(searchedCategoriesList, secondaryCategories);
      const { primaryCategory, thirdLevelCategory } = selectedCategories;
      const categoriesForValidation = [primaryCategory, id, thirdLevelCategory];
      setAdditionalBECategories((state) => ({ ...state, secondary: additionalSecondary }));
      setShouldResetSelectedCategories(false);
      checkAndUpdateValidationStatus(...categoriesForValidation);
      categoryDispatch({ type: actionTypes.SET_SELECTED_SECONDARY_CATEGORY, payload: id });
      categoryDispatch({ type: actionTypes.SET_SELECTED_THIRD_LEVEL_CATEGORY, payload: undefined });
      requestAnimationFrame(() => setIsSecondaryListOpen(false));

      if (!isTripleCategory) {
        if (isDirty || forceDirty) {
          if (isCategoryDrivenEnabled) {
            const data = await getCategoryDrivenTemplateWithoutThirdLevel(
              selectedCategories.primaryCategory,
              id,
              SR_TYPE_CODE_MAP[srType],
            );
            onTemplateChanged?.({ template: data, isCategoryDrivenChangedTemplate: true });
          }
          handleSaveCategories({ ...selectedCategories, secondaryCategory: id });
          sessionStorage.setItem(SESSION_STORAGE.categoryIsSelected, true);
        }
      } else {
        setIsThirdListOpen(false);
        setIsThirdListReady(false);
        if (!isAutoComplete) {
          setTimeout(() => {
            setIsThirdListReady(true);
            requestOpenThirdLevelList();
          }, 150);
        }
      }
      setShowExitPrompt(true);
      if (handleDirty) {
        handleDirty({ secondaryCategoty: id });
      }
    },
    [
      secondaryCategories,
      selectedCategories,
      checkAndUpdateValidationStatus,
      categoryDispatch,
      isTripleCategory,
      handleDirty,
      isDirty,
      isCategoryDrivenEnabled,
      handleSaveCategories,
      srType,
      onTemplateChanged,
      requestOpenThirdLevelList,
    ],
  );

  const handleResetCategory = useCallback(
    (categoryType) => {
      setIsDirty(true);
      categoryDispatch({ type: categoryType, payload: undefined });
      if (categoryType === 'SET_PRIMARY_CATEGORY') {
        setClearedObj((prev) => ({ ...prev, primary: true }));
        handleSaveCategories({
          primaryCategory: undefined,
          secondaryCategory: undefined,
          thirdLevelCategory: undefined,
        });
      } else if (categoryType === 'SET_SECONDARY_CATEGORY') {
        setClearedObj((prev) => ({ ...prev, secondary: true, third: true }));
        handleSaveCategories({ ...selectedCategories, secondaryCategory: undefined, thirdLevelCategory: undefined });
      } else {
        setClearedObj((prev) => ({ ...prev, third: true }));
        handleSaveCategories({ ...selectedCategories, thirdLevelCategory: undefined });
      }
    },
    [handleSaveCategories, selectedCategories, categoryDispatch],
  );

  const { primaryCategoryValue, secondaryCategoryValue, thirdLevelCategoryValue } = useMemo(() => {
    const primaryList = isExistingSR ? primaryCategories : filteredPrimaryCategories;
    const secondaryList = isExistingSR ? secondaryCategories : filteredSecondaryCategories;
    const thirdLevelList = isExistingSR ? thirdLevelCategories : filteredThirdLevelCategories;

    return {
      primaryCategoryValue: getCurrentValue(
        selectedCategories.primaryCategory,
        sr?.category?.primaryCategory ?? sr?.primaryCategory,
        primaryList?.concat(additionalBECategories.primary),
      ),
      secondaryCategoryValue: getCurrentValue(
        selectedCategories.secondaryCategory,
        sr?.category?.secondaryCategory ?? sr?.secondaryCategory,
        secondaryList?.concat(additionalBECategories.secondary),
      ),
      thirdLevelCategoryValue: isInitializing
        ? null
        : getCurrentValue(
            selectedCategories.thirdLevelCategory,
            sr?.category?.thirdLevelCategory ?? sr?.thirdLevelCategory,
            thirdLevelList?.concat(additionalBECategories.third),
          ),
    };
  }, [
    isInitializing,
    isExistingSR,
    primaryCategories,
    filteredPrimaryCategories,
    secondaryCategories,
    filteredSecondaryCategories,
    thirdLevelCategories,
    filteredThirdLevelCategories,
    getCurrentValue,
    selectedCategories.primaryCategory,
    selectedCategories.secondaryCategory,
    selectedCategories.thirdLevelCategory,
    sr?.category?.primaryCategory,
    sr?.category?.secondaryCategory,
    sr?.category?.thirdLevelCategory,
    sr?.primaryCategory,
    sr?.secondaryCategory,
    sr?.thirdLevelCategory,
    additionalBECategories.primary,
    additionalBECategories.secondary,
    additionalBECategories.third,
  ]);

  useGetSuggestedCategories(DisplayPage.TicketPanel);

  const hiddenSelectOptionsPrimaryCategoriesInput = useMemo(
    () => ({
      list: primaryCategories,
      fieldId: FIRST_LEVEL_CATEGORY_FIELD_ID,
      value: primaryCategoryValue?.id,
      srType,
    }),
    [primaryCategories, primaryCategoryValue?.id, srType],
  );
  const hiddenSelectOptionsSecondaryCategoriesInput = useMemo(
    () => ({
      list: secondaryCategories,
      fieldId: SECOND_LEVEL_CATEGORY_FIELD_ID,
      value: secondaryCategoryValue?.id,
      srType,
      queryKeys: [primaryCategoryValue?.id],
    }),
    [primaryCategoryValue?.id, secondaryCategories, secondaryCategoryValue?.id, srType],
  );
  const hiddenSelectOptionsThirdLevelCategoriesInput = useMemo(
    () => ({
      list: thirdLevelCategories,
      fieldId: THIRD_LEVEL_CATEGORY_FIELD_ID,
      value: thirdLevelCategoryValue?.id,
      srType,
      queryKeys: [primaryCategoryValue?.id, secondaryCategoryValue?.id],
    }),
    [primaryCategoryValue?.id, secondaryCategoryValue?.id, thirdLevelCategories, thirdLevelCategoryValue?.id, srType],
  );
  useHiddenSelectOption(hiddenSelectOptionsPrimaryCategoriesInput);
  useHiddenSelectOption(hiddenSelectOptionsSecondaryCategoriesInput);
  useHiddenSelectOption(hiddenSelectOptionsThirdLevelCategoriesInput);

  useEffect(() => {
    if (isInitializing) return;

    const srPrimaryCategoryValue = selectedCategories.primaryCategory;
    const srSecondaryCategoryValue = selectedCategories.secondaryCategory;
    const srThirdLevelCategoryValue = selectedCategories.thirdLevelCategory;

    let primaryCategory = srPrimaryCategoryValue;
    if (clearedObj.primary) primaryCategory = undefined;
    let secondaryCategory = srSecondaryCategoryValue;
    if (clearedObj.secondary) secondaryCategory = undefined;
    let thirdLevelCategory = srThirdLevelCategoryValue;
    if (clearedObj.third) thirdLevelCategory = undefined;

    const initialValues = {
      primaryCategory,
      secondaryCategory,
      thirdLevelCategory,
    };
    const endVals = {};
    if (clearedObj.primary) {
      //..do nothing
    } else if (srPrimaryCategoryValue != null || String(srPrimaryCategoryValue) === String(DELETED_CATEGORY_ID)) {
      categoryDispatch({ type: actionTypes.SET_SELECTED_PRIMARY_CATEGORY, payload: srPrimaryCategoryValue });
      endVals.first = srPrimaryCategoryValue;
    } else if (!isExistingSR || isTemplatePage) {
      endVals.first = firstLevelCategoryDefault || null;
    }
    if (clearedObj.secondary) {
      //..do nothing
    } else if (srSecondaryCategoryValue != null || String(srSecondaryCategoryValue) === String(DELETED_CATEGORY_ID)) {
      endVals.second = srSecondaryCategoryValue;
      categoryDispatch({ type: actionTypes.SET_SELECTED_SECONDARY_CATEGORY, payload: srSecondaryCategoryValue });
    } else if (!isExistingSR || isTemplatePage) {
      endVals.second = secondLevelCategoryDefault || null;
    }
    if (isTripleCategory) {
      if (clearedObj.third) {
        //..do nothing
      } else if (
        srThirdLevelCategoryValue != null ||
        String(srThirdLevelCategoryValue) === String(DELETED_CATEGORY_ID)
      ) {
        categoryDispatch({ type: actionTypes.SET_SELECTED_THIRD_LEVEL_CATEGORY, payload: srThirdLevelCategoryValue });
        endVals.third = srThirdLevelCategoryValue;
      } else if (!isExistingSR || isTemplatePage) {
        endVals.third = thirdLevelCategoryDefault || null;
      }
    }
    categoryDispatch({
      type: actionTypes.SET_ALL,
      payload: {
        primaryCategory: endVals.first,
        secondaryCategory: endVals.second,
        thirdLevelCategory: endVals.third,
      },
    });
    checkAndUpdateValidationStatus(
      initialValues.primaryCategory,
      initialValues.secondaryCategory,
      initialValues.thirdLevelCategory,
    );
  }, [
    isInitializing,
    clearedObj.primary,
    clearedObj.secondary,
    clearedObj.third,
    dispatch,
    categoryDispatch,
    isTemplatePage,
    checkAndUpdateValidationStatus,
    isExistingSR,
    isTripleCategory,
    firstLevelCategoryDefault,
    secondLevelCategoryDefault,
    thirdLevelCategoryDefault,
    selectedCategories.primaryCategory,
    selectedCategories.secondaryCategory,
    selectedCategories.thirdLevelCategory,
  ]);

  useEffect(() => {
    if (!isExistingSR && sr?.category?.firstLevelKey === -1) {
      setShouldResetSelectedCategories(true);
    }
  }, [isExistingSR, sr?.category?.firstLevelKey]);

  useEffect(() => {
    if (
      !isExistingSR &&
      selectedCategories.primaryCategory != null &&
      shouldResetSelectedCategories &&
      !isInitializing
    ) {
      if (firstLevelCategoryDefault || secondLevelCategoryDefault || thirdLevelCategoryDefault) {
        categoryDispatch({
          type: actionTypes.SET_ALL,
          payload: {
            primaryCategory: firstLevelCategoryDefault,
            secondaryCategory: secondLevelCategoryDefault,
            thirdLevelCategory: thirdLevelCategoryDefault,
          },
        });
      } else {
        categoryDispatch({ type: actionTypes.RESET_ALL });
      }
      setShouldResetSelectedCategories(false);
    }
  }, [
    isExistingSR,
    selectedCategories.primaryCategory,
    shouldResetSelectedCategories,
    isInitializing,
    firstLevelCategoryDefault,
    secondLevelCategoryDefault,
    thirdLevelCategoryDefault,
    categoryDispatch,
  ]);

  useEffect(() => {
    if (clearedObj.secondary === true || isInitializing || isTransitioning) return;
    if (
      secondaryCategoriesMemo?.length &&
      selectedCategories.primaryCategory &&
      typeof selectedCategories.secondaryCategory !== 'number'
    ) {
      if (secondaryCategoriesMemo?.length === 1) {
        setTimeout(() => {
          handleChangeSecondaryCategory(secondaryCategoriesMemo[0].id, secondaryCategories, true, true);
          if (isTripleCategory) {
            setTimeout(() => {
              const currentThirdLevelCategories = thirdLevelCategoriesMemo;
              if (currentThirdLevelCategories?.length === 1) {
                handleChangeThirdLevelCategory(currentThirdLevelCategories[0].id, thirdLevelCategories, true);
              } else if (currentThirdLevelCategories?.length > 1) {
                setIsThirdListReady(true);
                requestOpenThirdLevelList();
              }
            }, 300);
          }
        }, 200);
      } else {
        setTimeout(() => {
          setIsSecondaryListReady(true);
        }, 50);
      }
    }
  }, [
    handleChangeSecondaryCategory,
    clearedObj,
    required,
    secondaryCategoriesMemo,
    secondaryCategories,
    selectedCategories,
    isInitializing,
    isTransitioning,
    isTripleCategory,
    thirdLevelCategoriesMemo,
    thirdLevelCategories,
    handleChangeThirdLevelCategory,
    requestOpenThirdLevelList,
  ]);

  useEffect(() => {
    if (isInitializing) return;
    if (
      secondaryCategoriesMemo?.length >= 1 &&
      sr?.category?.secondLevelKey &&
      typeof sr?.category?.secondLevelKey !== 'number'
    ) {
      handleChangeSecondaryCategory(sr.category.secondLevelKey);
    }
  }, [sr, handleChangeSecondaryCategory, secondaryCategoriesMemo?.length, isInitializing]);

  useEffect(() => {
    if (
      clearedObj.third === true ||
      isInitializing ||
      isTransitioning ||
      !isTripleCategory ||
      typeof selectedCategories.secondaryCategory !== 'number' ||
      typeof selectedCategories.thirdLevelCategory === 'number'
    )
      return;

    if (thirdLevelCategoriesMemo?.length >= 1) {
      if (thirdLevelCategoriesMemo.length === 1) {
        setTimeout(() => {
          handleChangeThirdLevelCategory(thirdLevelCategoriesMemo[0].id, thirdLevelCategories, true);
        }, 200);
      } else {
        setTimeout(() => {
          setIsThirdListReady(true);
          requestOpenThirdLevelList();
        }, 150);
      }
    }
  }, [
    thirdLevelCategoriesMemo,
    thirdLevelCategories,
    selectedCategories.secondaryCategory,
    selectedCategories.thirdLevelCategory,
    isTripleCategory,
    handleChangeThirdLevelCategory,
    clearedObj.third,
    isInitializing,
    isTransitioning,
    requestOpenThirdLevelList,
  ]);

  useEffect(() => {
    if (clearedObj.third === true || typeof selectedCategories.thirdLevelCategory === 'number' || isInitializing)
      return;
    if (isTripleCategory) {
      if (
        thirdLevelCategoriesMemo?.length >= 1 &&
        sr?.category?.thirdLevelKey &&
        typeof sr?.category?.thirdLevelKey !== 'number'
      ) {
        handleChangeThirdLevelCategory(sr.category.thirdLevelKey, thirdLevelCategories);
      }
    }
  }, [
    sr,
    clearedObj,
    handleChangeThirdLevelCategory,
    isTripleCategory,
    thirdLevelCategories,
    thirdLevelCategoriesMemo?.length,
    selectedCategories.thirdLevelCategory,
    isInitializing,
  ]);

  useEffect(() => {
    const handleUnload = () => {
      if (showExitPrompt) {
        const e = window.event;
        e.preventDefault();
        if (e) {
          e.returnValue = false;
          return '';
        }
      }
    };

    window.addEventListener('beforeunload', handleUnload);
    return () => window.removeEventListener('beforeunload', handleUnload);
  }, [showExitPrompt]);

  const dropdownStyles = useMemo(
    () => ({
      backgroundColor: theme.palette.blueLabels.bt_cyan_reg,
      color: theme.palette.background.autocompleteSecondary,
      fontFamily: 'Roboto-Regular',
      borderRadius: '16px',
    }),
    [theme],
  );

  const getDefaultTitle = useCallback(
    (value) => {
      switch (value) {
        case 'firstLevel':
          if (isTemplatePage) {
            return categoryText;
          }
          return selectCategoryText;
        case 'secondLevel':
          if (isTemplatePage) {
            return subCategoryText;
          }
          return selectSubCategoryText;
        case 'thirdLevel':
          if (isTemplatePage) {
            return thirdLevelCategoryText;
          }
          return select3rdLevelCategoryText;
        default:
          return '';
      }
    },
    [
      isTemplatePage,
      categoryText,
      selectCategoryText,
      subCategoryText,
      selectSubCategoryText,
      thirdLevelCategoryText,
      select3rdLevelCategoryText,
    ],
  );

  const isSecondaryDisabled = disabled || !selectedCategories.primaryCategory;
  const isThirdDisabled = disabled || !selectedCategories.primaryCategory || !selectedCategories.secondaryCategory;

  const handleFirstLevelChange = useCallback(
    (id, list) => {
      setIsDirty(true);
      setIsInitializing(false);
      setIsTransitioning(true);
      setTimeout(() => {
        handleChangePrimaryCategory(id, list);
        setIsTransitioning(false);
      }, 200);
    },
    [handleChangePrimaryCategory],
  );

  const handleSecondLevelChange = useCallback(
    (id, list) => {
      setIsDirty(true);
      setIsTransitioning(true);
      setTimeout(() => {
        handleChangeSecondaryCategory(id, list, true, false); // false = manual selection
        setIsTransitioning(false);
      }, 200);
    },
    [handleChangeSecondaryCategory],
  );

  const handleThirdLevelChange = useCallback(
    (id, list) => {
      setIsDirty(true);
      setIsTransitioning(true);
      setTimeout(() => {
        handleChangeThirdLevelCategory(id, list, true);
        setIsTransitioning(false);
      }, 200);
    },
    [handleChangeThirdLevelCategory],
  );

  const addCategoryIfMissing = (currentList, id, name) => {
    let newList = currentList;

    if (!currentList.find((category) => category.id === id)) {
      newList = [...currentList, { id, name, valueKey: id }];
    }

    return newList;
  };

  const handleAllLevelChange = useCallback(
    ({ firstLevelKey, secondLevelKey, thirdLevelKey, primaryCategory, secondaryCategory, thirdLevelCategory }) => {
      setIsDirty(true);

      requestAnimationFrame(() => {
        setClearedObj({});
        const { primary, secondary, third } = additionalBECategories;

        const newPrimary = addCategoryIfMissing(primary, firstLevelKey, primaryCategory);
        const newSecondary = addCategoryIfMissing(secondary, secondLevelKey, secondaryCategory);
        const newThird = addCategoryIfMissing(third, thirdLevelKey, thirdLevelCategory);

        setAdditionalBECategories({
          primary: newPrimary,
          secondary: newSecondary,
          third: isTripleCategory ? newThird : third,
        });
        setIsPrimaryListOpen(false);
        setIsSecondaryListOpen(false);
        setIsThirdListOpen(false);

        setShouldResetSelectedCategories(false);
        checkAndUpdateValidationStatus([firstLevelKey, secondLevelKey, thirdLevelKey]);
        categoryDispatch({
          type: actionTypes.SET_ALL,
          payload: {
            primaryCategory: firstLevelKey,
            secondaryCategory: secondLevelKey,
            thirdLevelCategory: thirdLevelKey,
          },
        });
        categoryDispatch({ type: actionTypes.SET_SELECTED_PRIMARY_CATEGORY, payload: firstLevelKey });
        categoryDispatch({ type: actionTypes.SET_SELECTED_SECONDARY_CATEGORY, payload: secondLevelKey });

        if (!isTripleCategory) {
          handleSaveCategories({ primaryCategory: firstLevelKey, secondaryCategory: secondLevelKey });
        } else {
          categoryDispatch({ type: actionTypes.SET_SELECTED_THIRD_LEVEL_CATEGORY, payload: thirdLevelKey });
          handleSaveCategories({
            primaryCategory: firstLevelKey,
            secondaryCategory: secondLevelKey,
            thirdLevelCategory: thirdLevelKey,
          });
        }
        sessionStorage.setItem(SESSION_STORAGE.categoryIsSelected, true);

        if (handleDirty) {
          handleDirty({ primaryCategory: firstLevelKey, secondaryCategory: secondLevelKey });
        }
      });
    },
    [
      isTripleCategory,
      checkAndUpdateValidationStatus,
      handleDirty,
      handleSaveCategories,
      additionalBECategories,
      categoryDispatch,
    ],
  );

  const {
    virtualizedScrollContextValue,
    selectOptionsWrapperClassName,
    dropdownHeightExtension,
    indexOffset,
    isSuggestedCategoryShown,
  } = useSuggestedCategoryDropdownControl({
    srId: sr?.id,
    currentCategory: {
      primaryId: primaryCategoryValue?.id,
      secondaryId: secondaryCategoryValue?.id,
      thirdLevelId: thirdLevelCategoryValue?.id,
    },
    selectSuggestedCategoryProps: {
      srId: sr?.id,
      panelHandlerOptions: {
        handleAllLevelChange,
      },
    },
  });

  const dropdownProps = useMemo(
    () => ({
      additionalWrapperClassName: selectOptionsWrapperClassName,
      dropdownHeightExtension,
      indexOffset,
    }),
    [selectOptionsWrapperClassName, dropdownHeightExtension, indexOffset],
  );

  const firstLevelOptions = useMemo(
    () => filteredPrimaryCategories?.map((category) => ({ ...category, customStyle: dropdownStyles })),
    [filteredPrimaryCategories, dropdownStyles],
  );

  const secondLevelOptions = useMemo(
    () => filteredSecondaryCategories?.map((category) => ({ ...category, customStyle: dropdownStyles })),
    [filteredSecondaryCategories, dropdownStyles],
  );

  const thirdLevelOptions = useMemo(
    () => filteredThirdLevelCategories?.map((category) => ({ ...category, customStyle: dropdownStyles })),
    [filteredThirdLevelCategories, dropdownStyles],
  );

  const isClearDisabled = useMemo(() => !isTemplatePage && !isNewSR, [isTemplatePage, isNewSR]);

  const primaryFocusWrapper = useCallback(() => {
    // Update local state when the user opens the dropdowns manually
    setIsPrimaryListOpen(true);
    onFocus?.(FIRST_LEVEL_CATEGORY_FIELD_ID);
  }, [onFocus]);

  const secondaryFocusWrapper = useCallback(() => {
    setIsSecondaryListOpen(true);
    onFocus?.(SECOND_LEVEL_CATEGORY_FIELD_ID);
  }, [onFocus]);

  const primaryOnClose = useCallback(() => {
    setIsPrimaryListOpen(false);
  }, []);

  const secondaryOnClose = useCallback(() => {
    setIsSecondaryListOpen(false);
  }, []);

  const thirdLevelOnClose = useCallback(() => {
    setIsThirdListOpen(false);
  }, []);

  const handleFirstLevelClearValue = useCallback(
    () => handleResetCategory('SET_PRIMARY_CATEGORY'),
    [handleResetCategory],
  );
  const handleSecondLevelClearValue = useCallback(
    () => handleResetCategory('SET_SECONDARY_CATEGORY'),
    [handleResetCategory],
  );
  const handleThirdLevelClearValue = useCallback(
    () => handleResetCategory('SET_THIRD_LEVEL_CATEGORY'),
    [handleResetCategory],
  );

  return (
    <StyledCategories>
      <Container>
        <LabelWrap>
          <HeaderItemLabel className={className}>{categoriesText}</HeaderItemLabel>
          <div className="categories">
            <VirtualizedScrollContext.Provider value={virtualizedScrollContextValue}>
              <CategorySelect
                onChange={handleFirstLevelChange}
                list={firstLevelOptions}
                initValue={primaryCategoryValue}
                customHeaderStyles={(selectedValue) =>
                  getCustomHeaderStyles(selectedValue, required, forceShowValidation)
                }
                defaultTitle={getDefaultTitle('firstLevel')}
                isLoading={isLoading}
                disabled={disabled}
                handleClearValue={handleFirstLevelClearValue}
                isTemplatePage={isTemplatePage}
                fieldId={categoryFieldsMap?.['category.firstLevelKey']?.fieldId}
                dataset={dataset}
                isOpen={isPrimaryListOpen}
                onFocus={primaryFocusWrapper}
                onClose={primaryOnClose}
                isSuggestedCategoryShown={isSuggestedCategoryShown}
                dropdownProps={dropdownProps}
                className={className}
                required={required}
                isNewField={!isExistingSR}
                onDisabledClick={onDisabledClick}
              />
              <CategorySelect
                onChange={handleSecondLevelChange}
                initValue={secondaryCategoryValue}
                isLoading={isLoading}
                list={secondLevelOptions}
                customHeaderStyles={(selectedValue) =>
                  getCustomHeaderStyles(selectedValue, required, forceShowValidation)
                }
                defaultTitle={getDefaultTitle('secondLevel')}
                isOpen={isSecondaryListOpen}
                disabled={isSecondaryDisabled}
                handleClearValue={handleSecondLevelClearValue}
                isTemplatePage={isTemplatePage}
                fieldId={categoryFieldsMap?.['category.secondLevelKey']?.fieldId}
                dataset={dataset}
                onFocus={secondaryFocusWrapper}
                onClose={secondaryOnClose}
                dropdownProps={dropdownProps}
                className={className}
                required={required}
                isNewField={!isExistingSR}
                onDisabledClick={onDisabledClick}
                isClearDisabled={isClearDisabled}
                clearTooltipTitle={isClearDisabled && secondCategoryClearDisabledTooltipText}
              />
              {isTripleCategory && (
                <CategorySelect
                  onChange={handleThirdLevelChange}
                  initValue={thirdLevelCategoryValue}
                  isLoading={isLoading}
                  list={thirdLevelOptions}
                  customHeaderStyles={(selectedValue) =>
                    getCustomHeaderStyles(selectedValue, required, forceShowValidation)
                  }
                  defaultTitle={getDefaultTitle('thirdLevel')}
                  isOpen={isThirdListOpen}
                  disabled={isThirdDisabled}
                  handleClearValue={handleThirdLevelClearValue}
                  isTemplatePage={isTemplatePage}
                  fieldId={categoryFieldsMap?.['category.thirdLevelKey']?.fieldId}
                  dataset={dataset}
                  onFocus={thirdLevelFocusWrapper}
                  onClose={thirdLevelOnClose}
                  dropdownProps={dropdownProps}
                  className={className}
                  required={required}
                  isNewField={!isExistingSR}
                  onDisabledClick={onDisabledClick}
                  isClearDisabled={isClearDisabled}
                  clearTooltipTitle={isClearDisabled && thirdCategoryClearDisabledTooltipText}
                />
              )}
            </VirtualizedScrollContext.Provider>
          </div>
        </LabelWrap>
      </Container>
    </StyledCategories>
  );
}
export default memo(Categories);

Categories.propTypes = {
  disabled: PropTypes.bool,
  onDisabledClick: PropTypes.func,
  dataset: PropTypes.object,
  isTemplatePage: PropTypes.bool,
  headerFields: PropTypes.arrayOf(PropTypes.object),
  srType: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  handleDirty: PropTypes.func,
  onFocus: PropTypes.func,
  handleSaveValue: PropTypes.func.isRequired,
  required: PropTypes.bool,
  updateFieldValidation: PropTypes.func,
  className: PropTypes.string,
  categoriesValues: PropTypes.shape({
    primaryCategory: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    secondaryCategory: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    thirdLevelCategory: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  forceSrId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onTemplateChanged: PropTypes.func,
  isNewSR: PropTypes.bool,
};
